import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Mail, AlertTriangle, Check, Plus, X } from 'lucide-react';
import { getAccessToken } from '../services/auth';

// EMAIL DELIVERABILITY - the Email Reputation tab on SEO & AEO.
//
// Two questions, because the newsletters and the win-back invites leave by different doors:
//   * Google's view of each DOMAIN (Postmaster Tools v2): does it meet Google's sender
//     requirements, and what are its daily spam, DMARC and delivery-error rates.
//   * Each REP's reply rate on the win-back invites, which go out one at a time from their own
//     Microsoft 365 mailbox. Those are a sliver of crabplace.com's Gmail traffic, so Google's
//     domain figures cannot see one mailbox going bad; a reply rate collapsing can.
//
// ⚠️ This used to show Google's High/Medium/Low reputation score. Google turned that API off in
// September 2026 and v2 has no such score, so nothing here pretends one exists.
// ⚠️ Google writes a flat 0 for a day it has not finished counting, so a DMARC 0 is shown as
// "not counted", never as 0% passing.
// ⚠️ Derives nothing: every figure comes from deliverability-check.js, so the page and the
// daily email cannot disagree.

const FN = '/.netlify/functions/deliverability-check';
const NEON = '/.netlify/functions/neon';

async function call(body) {
  const headers = { 'Content-Type': 'application/json' };
  const token = getAccessToken();
  if (token) headers.Authorization = 'Bearer ' + token;
  const res = await fetch(FN, { method: 'POST', headers, body: JSON.stringify(body || {}) });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.error || 'deliverability ' + res.status);
  return j;
}
const neon = (query, params) => fetch(NEON, {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ query, params }),
}).then((r) => r.json());

const LABEL = {
  SPF: 'SPF', DKIM: 'DKIM', SPF_AND_DKIM: 'SPF and DKIM', DMARC_POLICY: 'DMARC policy',
  DMARC_ALIGNMENT: 'DMARC alignment', MESSAGE_FORMATTING: 'Message formatting', DNS_RECORDS: 'DNS records',
  ENCRYPTION: 'Encryption', USER_REPORTED_SPAM_RATE: 'Spam complaints',
  ONE_CLICK_UNSUBSCRIBE: 'One-click unsubscribe', HONOR_UNSUBSCRIBE: 'Honours unsubscribes',
};
const pct = (x, dp = 2) => (x == null ? '-' : (x * 100).toFixed(dp) + '%');
const fmtDay = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-';
};
const first = (s) => String(s || '').split(' ')[0];

const card = { background: 'var(--white)', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 14, marginBottom: 12 };
const th = { textAlign: 'left', fontSize: 11, color: 'var(--gray-500)', fontWeight: 600, padding: '4px 8px', borderBottom: '1px solid var(--gray-200)' };
const td = { fontSize: 12, color: 'var(--gray-700)', padding: '4px 8px', borderBottom: '1px solid var(--gray-100)' };

// initialData exists for scripts/test-render-panels.js, which renders on the server where the
// loading effect never runs - without it only the empty state could ever be tested.
export default function EmailDeliverability({ initialData = null } = {}) {
  const [data, setData] = useState(initialData);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const [newDomain, setNewDomain] = useState('');

  const load = useCallback(async (action) => {
    setErr(null);
    if (action) setBusy(true);
    try { setData(await call(action ? { action } : {})); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { if (!initialData) load(); }, [load, initialData]);

  const addDomain = async () => {
    const d = newDomain.trim().toLowerCase();
    if (!d) return;
    await neon('postmaster_config_add', { domain: d });
    setNewDomain('');
    load('run');
  };
  const removeDomain = async (d) => {
    if (!window.confirm('Stop monitoring ' + d + '?')) return;
    await neon('postmaster_config_remove', { domain: d });
    load();
  };

  const run = data && data.run;
  const compliance = (run && run.compliance) || [];
  const reps = (run && run.reps) || [];
  const problems = (run && run.problems) || [];
  const daysFor = (d) => (data ? data.daily.filter((x) => x.domain === d) : []);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={{ fontSize: 12, color: 'var(--gray-700)', maxWidth: 760 }}>
          Checked every morning. CPx emails <b>greg@crabplace.com</b> when something needs attention,
          including when the check itself cannot reach Google.
          {run && <span style={{ color: 'var(--gray-500)' }}> Last checked {run.finished}.</span>}
        </span>
        <button className="btn btn-primary" onClick={() => load('run')} disabled={busy} style={{ fontSize: 12, padding: '6px 12px' }}>
          <RefreshCw size={13} style={{ marginRight: 4, ...(busy ? { animation: 'spin 1s linear infinite' } : {}) }} />
          {busy ? 'Checking...' : 'Check Now'}
        </button>
      </div>

      {err && <div style={{ ...card, borderLeft: '4px solid var(--red)', color: 'var(--red)', fontSize: 13 }}>{err}</div>}
      {data && !run && <div style={{ ...card, fontSize: 13 }}>No check has run yet. Press <b>Check Now</b>.</div>}

      {run && run.error && (
        <div style={{ ...card, borderLeft: '4px solid var(--red)' }}>
          <div style={{ fontWeight: 700, color: 'var(--red)', fontSize: 13 }}>
            <AlertTriangle size={14} style={{ verticalAlign: -2, marginRight: 6 }} />The last check could not finish
          </div>
          <div style={{ fontSize: 12, color: 'var(--gray-700)', marginTop: 4 }}>{run.error}</div>
          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4 }}>Anything it could not read is unknown, not fine.</div>
        </div>
      )}

      {run && problems.length > 0 && (
        <div style={{ ...card, borderLeft: '4px solid var(--orange)' }}>
          <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 13, marginBottom: 6 }}>Needs a look</div>
          {problems.map((p) => (
            <div key={p.key} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>{p.title}</div>
              <div style={{ fontSize: 12, color: 'var(--gray-700)' }}>{p.detail}</div>
            </div>
          ))}
        </div>
      )}
      {run && !run.error && problems.length === 0 && (
        <div style={{ ...card, borderLeft: '4px solid var(--green)', fontSize: 13, color: 'var(--navy)' }}>
          <Check size={14} style={{ verticalAlign: -2, marginRight: 6, color: 'var(--green)' }} />
          Nothing needs attention. Every domain meets Google's requirements and no rep's replies have dropped.
        </div>
      )}

      {data && data.domains.map((domain) => {
        const c = compliance.find((x) => x.domain === domain);
        const ok = c && c.verdict === 'COMPLIANT' && !(c.requirements || []).some((r) => r.status !== 'COMPLIANT');
        const days = daysFor(domain);
        const settled = days.slice(0, -2);
        const last = settled[settled.length - 1];
        return (
          <div key={domain} style={{ ...card, borderLeft: '4px solid ' + (c ? (ok ? 'var(--green)' : 'var(--orange)') : 'var(--gray-300)') }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <Mail size={15} style={{ color: 'var(--navy)' }} />
                  <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--navy)' }}>{domain}</span>
                  <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700,
                    background: c ? (ok ? 'var(--green)' : 'var(--orange)') : 'var(--gray-200)',
                    color: c ? 'var(--white)' : 'var(--gray-600)' }}>
                    {c ? (ok ? 'Meets Google\'s Requirements' : 'Needs Work') : 'No Reading'}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 6 }}>
                  {last ? 'Spam rate ' + pct(last.spam) + ' on ' + fmtDay(last.date) + ' (Google\'s line is 0.30%)' : 'No daily figures yet'}
                  {c && c.verdictReason && ' · Google says: ' + c.verdictReason.replace(/_/g, ' ').toLowerCase()}
                </div>
                {c && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                    {(c.requirements || []).map((r) => (
                      <span key={r.requirement} title={r.status}
                        style={{ fontSize: 11, padding: '1px 8px', borderRadius: 10, border: '1px solid ' + (r.status === 'COMPLIANT' ? 'var(--gray-200)' : 'var(--orange)'),
                          color: r.status === 'COMPLIANT' ? 'var(--gray-600)' : 'var(--orange)' }}>
                        {r.status === 'COMPLIANT' ? '✓ ' : '! '}{LABEL[r.requirement] || r.requirement}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-ghost" onClick={() => setOpen(open === domain ? null : domain)} style={{ fontSize: 11, padding: '4px 10px' }}>
                  {open === domain ? 'Hide Days' : 'Daily Figures'}
                </button>
                <button className="btn btn-ghost" onClick={() => removeDomain(domain)} style={{ fontSize: 11, padding: '4px 10px', color: 'var(--red)' }}>
                  <X size={12} />
                </button>
              </div>
            </div>
            {open === domain && (
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 10 }}>
                <thead><tr><th style={th}>Day</th><th style={th}>Spam rate</th><th style={th}>DMARC passing</th><th style={th}>Delivery errors</th></tr></thead>
                <tbody>
                  {[...days].reverse().map((d, i) => {
                    const counting = i < 2 || (d.dmarc === 0 && !d.spam && !d.errors);
                    return (
                      <tr key={d.date}>
                        <td style={td}>{fmtDay(d.date)}</td>
                        <td style={td}>{counting && !d.spam ? <span style={{ color: 'var(--gray-500)' }}>not counted</span> : pct(d.spam)}</td>
                        <td style={td}>{counting ? <span style={{ color: 'var(--gray-500)' }}>not counted</span> : pct(d.dmarc, 0)}</td>
                        <td style={td}>{counting && !d.errors ? <span style={{ color: 'var(--gray-500)' }}>not counted</span> : pct(d.errors, 3)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        );
      })}

      {reps.length > 0 && (
        <div style={card}>
          <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 13 }}>Win-back and invite emails, by rep</div>
          <div style={{ fontSize: 11, color: 'var(--gray-500)', margin: '2px 0 8px' }}>
            These go out from each rep's own mailbox, which Google's domain figures above cannot see.
            Emails sent 3 to 10 days ago, so the replies have had time to arrive. Follow-ups are left out of the reply rates.
            Replies are flagged only when a rep falls under a third of their usual <i>and</i> under half of the rest of the team,
            and not while their emails are still being opened: an aging list slows everyone at once, a mailbox going to Junk slows one rep.
            Opens are flagged under 60% of the rep's usual and of the team's.
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Rep</th><th style={th}>First win-backs: replied (usual / team)</th><th style={th}>Invites: replied (usual / team)</th><th style={th}>Opened (usual / team)</th><th style={th}></th></tr></thead>
            <tbody>
              {reps.map((r) => {
                // Runs before Oct 20, 2026 stored one rate per rep, with no kinds or opens.
                const legacy = !r.kinds;
                const cell = (k) => (!k ? '-' : k.recentSent + ' sent, ' + (k.recentRate == null ? '-' : k.recentRate + '%')
                  + ' (' + (k.typicalRate == null ? '-' : k.typicalRate + '%') + ' / ' + (k.teamRate == null ? '-' : k.teamRate + '%') + ')');
                const kind = (name) => (r.kinds || []).find((k) => k.kind === name);
                const red = (on) => (on ? { color: 'var(--red)', fontWeight: 700 } : null);
                const thin = !legacy && (r.kinds || []).every((k) => k.recentSent < 40) && (r.recentTracked || 0) < 40;
                return (
                  <tr key={r.rep}>
                    <td style={{ ...td, fontWeight: 600, color: 'var(--navy)' }}>{first(r.rep)}</td>
                    {legacy ? (
                      <td style={td} colSpan={3}>{r.recentSent} sent, {r.recentRate == null ? '-' : r.recentRate + '%'} replied (usual {r.typicalRate == null ? '-' : r.typicalRate + '%'}), all kinds together</td>
                    ) : (
                      <>
                        <td style={{ ...td, ...red(kind('winback') && kind('winback').flagged) }}>{cell(kind('winback'))}</td>
                        <td style={{ ...td, ...red(kind('invite') && kind('invite').flagged) }}>{cell(kind('invite'))}</td>
                        <td style={{ ...td, ...red(r.opensFlagged) }}>{r.openRate == null ? '-' : r.openRate + '%'} ({r.typicalOpenRate == null ? '-' : r.typicalOpenRate + '%'} / {r.teamOpenRate == null ? '-' : r.teamOpenRate + '%'})</td>
                      </>
                    )}
                    <td style={{ ...td, color: r.flagged ? 'var(--red)' : 'var(--gray-500)', fontWeight: r.flagged ? 700 : 400 }}>
                      {r.flagged ? 'Dropped' : (legacy ? r.recentSent < 40 : thin) ? 'Too few to judge' : 'Normal'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={card}>
        <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 13, marginBottom: 6 }}>Add a Monitored Domain</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input className="form-input" placeholder="e.g. crabplace.com" value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addDomain(); }}
            style={{ maxWidth: 260 }} />
          <button className="btn btn-outline" onClick={addDomain} style={{ fontSize: 12 }}>
            <Plus size={13} style={{ marginRight: 4 }} />Add Domain
          </button>
        </div>
        <p style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 8 }}>
          It must already be verified in Google Postmaster Tools, with the CPx service account
          (crabplace-ga4-reader@gen-lang-client-0536509589.iam.gserviceaccount.com) added as a user.
        </p>
      </div>
    </div>
  );
}
